/**
 * Imprimir: el recibo en papel.
 *
 * A pulpería's printer is a cheap thermal one, usually Bluetooth, on a 58mm roll, and a phone
 * reaches it through the operating system's print service, the same way it reaches any printer.
 * So this prints the text the receipt already is — thirty-two characters a line, which is a
 * 58mm roll exactly — in a page of its own sized to the roll, through a hidden frame so the app
 * itself never shows up on paper.
 *
 * The roll's width belongs to this device, like the language: the counter's printer and the
 * driver's are different printers, and neither is anybody else's business.
 */

export type Papel = '58' | '80';

const CLAVE = 'abastece.papel';

export function papelActual(): Papel {
  try {
    return localStorage.getItem(CLAVE) === '80' ? '80' : '58';
  } catch {
    return '58';
  }
}

export function elegirPapel(papel: Papel): void {
  try {
    localStorage.setItem(CLAVE, papel);
  } catch {
    // A device that refuses storage prints on 58mm, the common roll. Harmless.
  }
}

/** What fits a line: the printable width of each roll, in millimetres. */
const IMPRIMIBLE: Record<Papel, number> = { '58': 48, '80': 72 };

const escapar = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The page. A monospace character is about 0.6 of its size wide, so thirty-two of them fill the
 * roll at this size and no wider.
 */
export function paginaDeImpresion(texto: string, papel: Papel): string {
  const letra = (IMPRIMIBLE[papel] / (32 * 0.6)).toFixed(2);
  const margen = ((Number(papel) - IMPRIMIBLE[papel]) / 2).toFixed(1);
  // The first line is the shop's name: what the print queue, or a saved PDF, gets called.
  const titulo = escapar(texto.split('\n', 1)[0] ?? '');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${titulo}</title><style>
@page { size: ${papel}mm auto; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; color: #000; }
pre { margin: 0; padding: 3mm ${margen}mm 6mm; font: ${letra}mm/1.3 'Courier New', ui-monospace, monospace; white-space: pre; }
</style></head><body><pre>${escapar(texto)}</pre></body></html>`;
}

/** A page that shows at a glance whether the roll and the setting agree. */
export function textoPrueba(negocio: string): string {
  return [
    negocio.toUpperCase(),
    'Prueba de impresora',
    '-'.repeat(32),
    '12345678901234567890123456789012',
    'Si la línea de números sale',
    'entera en un solo renglón, el',
    'ancho está bien.',
    '-'.repeat(32),
  ].join('\n');
}

export function imprimirTexto(texto: string, papel: Papel = papelActual()): void {
  const marco = document.createElement('iframe');
  marco.setAttribute('aria-hidden', 'true');
  marco.tabIndex = -1;
  marco.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  const quitar = () => marco.remove();
  marco.onload = () => {
    const ventana = marco.contentWindow;
    if (!ventana) return quitar();
    ventana.addEventListener('afterprint', () => setTimeout(quitar, 500), { once: true });
    ventana.focus();
    ventana.print();
    // The frame took the focus to print. Give it back, or the keys meant for the app — Escape on
    // the sheet still open — go to a page nobody can see.
    window.focus();
    // Some browsers never say the dialog closed. Do not leave the frame behind for ever.
    setTimeout(quitar, 120_000);
  };
  marco.srcdoc = paginaDeImpresion(texto, papel);
  document.body.appendChild(marco);
}
